/**
 * SmartMatching Page - Enhanced with priority grouping and new hooks
 * Phase 5 Part 2: Recommendations Dashboard UI
 * Phase 6: Integrated messaging
 */

import { useState, useMemo } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useSellerMatches, useMatchingStats, useBatchRecalculate } from '../../hooks/useMatching';
import { MatchCard } from '../../components/MatchCard';
import { MessageComposer } from '../../components/MessageComposer';
import { IntroductionRequestForm } from '../../components/IntroductionRequestForm';
import {
  Target,
  RefreshCw,
  Flame,
  Star,
  Eye,
  Clock,
  Filter,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Minus,
  Zap,
  X
} from 'lucide-react';

// Stat card component
function StatCard({ title, count, icon: Icon, color, description }) {
  return (
    <div className={`bg-white rounded-xl border-t-4 p-5 shadow-sm`} style={{ borderTopColor: color }}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{count}</p>
          {description && (
            <p className="text-xs text-gray-400 mt-1">{description}</p>
          )}
        </div>
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: color + '20' }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
      </div>
    </div>
  );
}

// Match section component
function MatchSection({ title, description, icon: Icon, iconColor, matches, emptyText, onAction }) {
  if (!matches || matches.length === 0) return null;

  return (
    <section className="mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: iconColor + '20' }}
        >
          <Icon className="w-4 h-4" style={{ color: iconColor }} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <span className="ml-auto px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full">
          {matches.length}
        </span>
      </div>
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
        {matches.map((match) => (
          <MatchCard
            key={match.id || `${match.seller_id}-${match.buyer_id}`}
            match={match}
            onAction={onAction}
          />
        ))}
      </div>
    </section>
  );
}

export default function SmartMatching() {
  const { user } = useAuth();
  const sellerId = user?.sellerId;

  // Filter state
  const [filter, setFilter] = useState('all');
  const [minScore, setMinScore] = useState(0); // Changed from 30 to show all matches
  const [viewMode, setViewMode] = useState('grouped'); // grouped or list

  // Message compose modal state
  const [composeTarget, setComposeTarget] = useState(null);

  // Introduction request modal state
  const [introTarget, setIntroTarget] = useState(null);

  // Use enhanced hooks
  const {
    matches,
    loading,
    error,
    totalCount,
    stats: matchStats,
    refresh
  } = useSellerMatches(sellerId, {
    minScore,
    autoFetch: true,
    realtime: true
  });

  const { stats: detailedStats, loading: statsLoading } = useMatchingStats(sellerId);
  const { recalculating, recalculateAll } = useBatchRecalculate(sellerId);

  // Filter matches based on selected filter
  // Adjusted thresholds for new weighted scoring (Activity=0 brings average down)
  const filteredMatches = useMemo(() => {
    if (!matches) return [];

    return matches.filter(match => {
      const score = match.final_score || match.match_score || 0;
      if (filter === 'all') return true;
      if (filter === 'excellent') return score >= 65;
      if (filter === 'strong') return score >= 55 && score < 65;
      if (filter === 'moderate') return score >= 40 && score < 55;
      // Hot = high score + either high market opportunity or high signal multiplier
      if (filter === 'hot') return score >= 55 && ((match.market_opportunity_score || 0) >= 40 || (match.signal_multiplier || 1) >= 1.1);
      return true;
    });
  }, [matches, filter]);

  // Group matches by priority
  // Adjusted thresholds for new weighted scoring (Activity=0 brings average down)
  const groupedMatches = useMemo(() => {
    if (!filteredMatches) return { urgent: [], highPriority: [], monitor: [], watch: [] };

    return {
      // Urgent = excellent score (65+) + either high market opportunity or high signals
      urgent: filteredMatches.filter(m => {
        const score = m.final_score || m.match_score || 0;
        return score >= 65 && ((m.market_opportunity_score || 0) >= 40 || (m.signal_multiplier || 1) >= 1.1);
      }),
      // High Priority = excellent score (65+) without urgent signals
      highPriority: filteredMatches.filter(m => {
        const score = m.final_score || m.match_score || 0;
        const marketOpp = m.market_opportunity_score || 0;
        const signalMult = m.signal_multiplier || 1;
        return score >= 65 && marketOpp < 40 && signalMult < 1.1;
      }),
      // Monitor = strong score (55-64)
      monitor: filteredMatches.filter(m => {
        const score = m.final_score || m.match_score || 0;
        return score >= 55 && score < 65;
      }),
      // Watch = moderate score (40-54)
      watch: filteredMatches.filter(m => {
        const score = m.final_score || m.match_score || 0;
        return score >= 40 && score < 55;
      })
    };
  }, [filteredMatches]);

  // Handle recalculate
  const handleRecalculate = async () => {
    await recalculateAll();
    refresh();
  };

  // Handle match card action (reach out, monitor, request intro, etc.)
  const handleMatchAction = (match, action) => {
    if (action === 'reach_out') {
      // Open compose modal with buyer info
      setComposeTarget({
        buyerId: match.buyer_id,
        buyerName: match.buyers?.company_name || match.company_name || 'Unknown Company',
        matchScore: match.final_score || match.match_score || 0
      });
    } else if (action === 'request_intro') {
      // Open introduction request modal - Pharmunity acts as intermediary
      setIntroTarget({
        buyer: {
          id: match.buyer_id,
          company_name: match.buyers?.company_name || match.company_name || 'Unknown Company',
          contact_name: match.buyers?.contact_name
        },
        matchScore: match.final_score || match.match_score || 0
      });
    }
    // Other actions can be handled here
  };

  // Handle introduction request sent
  const handleIntroSent = () => {
    setIntroTarget(null);
    // Could show a toast notification here
  };

  // Handle message sent
  const handleMessageSent = () => {
    setComposeTarget(null);
    // Could show a toast notification here
  };

  // Loading state
  if (loading && !matches?.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading matches...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Smart Matching</h1>
          <p className="text-gray-600 mt-1">
            AI-powered matching connects you with the right buyers for your assets
          </p>
        </div>
        <button
          onClick={handleRecalculate}
          disabled={recalculating}
          className="btn-primary flex items-center gap-2 whitespace-nowrap"
        >
          <RefreshCw className={`w-4 h-4 ${recalculating ? 'animate-spin' : ''}`} />
          {recalculating ? 'Calculating...' : 'Recalculate All'}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Urgent Action"
          count={groupedMatches.urgent.length}
          icon={Flame}
          color="#EF4444"
          description="High score + active interest"
        />
        <StatCard
          title="High Priority"
          count={groupedMatches.highPriority.length}
          icon={Star}
          color="#F59E0B"
          description="Excellent match, monitor activity"
        />
        <StatCard
          title="Monitor"
          count={groupedMatches.monitor.length}
          icon={Eye}
          color="#3B82F6"
          description="Strong matches to watch"
        />
        <StatCard
          title="Watch List"
          count={groupedMatches.watch.length}
          icon={Clock}
          color="#6B7280"
          description="Moderate matches"
        />
      </div>

      {/* Trend Stats */}
      {detailedStats && (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3 mb-3">
            <BarChart3 className="w-5 h-5 text-gray-400" />
            <h3 className="font-medium text-gray-900">Match Trends</h3>
          </div>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="flex items-center justify-center gap-1 text-green-600">
                <TrendingUp className="w-4 h-4" />
                <span className="text-xl font-bold">{detailedStats.byTrend?.improving || 0}</span>
              </div>
              <p className="text-xs text-gray-500">Improving</p>
            </div>
            <div>
              <div className="flex items-center justify-center gap-1 text-gray-500">
                <Minus className="w-4 h-4" />
                <span className="text-xl font-bold">{detailedStats.byTrend?.stable || 0}</span>
              </div>
              <p className="text-xs text-gray-500">Stable</p>
            </div>
            <div>
              <div className="flex items-center justify-center gap-1 text-red-500">
                <TrendingDown className="w-4 h-4" />
                <span className="text-xl font-bold">{detailedStats.byTrend?.declining || 0}</span>
              </div>
              <p className="text-xs text-gray-500">Declining</p>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-xl border border-gray-200 p-4">
        <Filter className="w-4 h-4 text-gray-400" />
        <span className="text-sm font-medium text-gray-700">Filter:</span>

        <div className="flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'All Matches' },
            { value: 'hot', label: 'Hot', icon: Zap },
            { value: 'excellent', label: 'Excellent (65+)' },
            { value: 'strong', label: 'Strong (55-64)' },
            { value: 'moderate', label: 'Moderate (40-54)' }
          ].map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                filter === f.value
                  ? 'bg-purple-100 text-purple-700 font-medium'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.icon && <f.icon className="w-3 h-3" />}
              {f.label}
              {f.value === 'all' && ` (${filteredMatches.length})`}
            </button>
          ))}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <label className="text-sm text-gray-500">View:</label>
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
            className="text-sm border-gray-300 rounded-lg focus:ring-purple-500 focus:border-purple-500"
          >
            <option value="grouped">Grouped</option>
            <option value="list">List</option>
          </select>
        </div>
      </div>

      {/* Match Sections */}
      {viewMode === 'grouped' ? (
        <>
          <MatchSection
            title="Urgent Action Needed"
            description="High match score + active buyer interest. Reach out immediately!"
            icon={Flame}
            iconColor="#EF4444"
            matches={groupedMatches.urgent}
            onAction={handleMatchAction}
          />

          <MatchSection
            title="High Priority Matches"
            description="Excellent compatibility. Monitor for buyer activity signals."
            icon={Star}
            iconColor="#F59E0B"
            matches={groupedMatches.highPriority}
            onAction={handleMatchAction}
          />

          <MatchSection
            title="Monitor These Matches"
            description="Strong matches worth tracking. Keep an eye on buyer signals."
            icon={Eye}
            iconColor="#3B82F6"
            matches={groupedMatches.monitor}
            onAction={handleMatchAction}
          />

          <MatchSection
            title="Watch List"
            description="Moderate matches. Track over time for improvements."
            icon={Clock}
            iconColor="#6B7280"
            matches={groupedMatches.watch}
            onAction={handleMatchAction}
          />
        </>
      ) : (
        <div className="space-y-4">
          {filteredMatches.map((match) => (
            <MatchCard
              key={match.id || `${match.seller_id}-${match.buyer_id}`}
              match={match}
              onAction={handleMatchAction}
            />
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredMatches.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Target className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No matches found</h3>
          <p className="text-gray-500 mb-6">
            {filter !== 'all'
              ? 'Try changing your filters to see more matches'
              : 'Matches will appear as buyers view your profile and interact with your company'}
          </p>
          {filter !== 'all' ? (
            <button onClick={() => setFilter('all')} className="btn-secondary">
              Clear Filters
            </button>
          ) : (
            <button onClick={handleRecalculate} className="btn-primary">
              Calculate Matches
            </button>
          )}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          <p className="font-medium">Error loading matches</p>
          <p className="text-sm mt-1">{error}</p>
          <button onClick={refresh} className="mt-3 btn-secondary text-sm">
            Try Again
          </button>
        </div>
      )}

      {/* Message Compose Modal */}
      {composeTarget && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setComposeTarget(null)}
            />

            {/* Modal */}
            <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform bg-white rounded-xl shadow-xl sm:my-16">
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Send Message to {composeTarget.buyerName}
                  </h3>
                  <p className="text-sm text-gray-500">
                    Match Score: {Math.round(composeTarget.matchScore)}
                  </p>
                </div>
                <button
                  onClick={() => setComposeTarget(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Message Composer */}
              <div className="p-6">
                <MessageComposer
                  sellerId={sellerId}
                  buyerId={composeTarget.buyerId}
                  onSuccess={handleMessageSent}
                  onCancel={() => setComposeTarget(null)}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Introduction Request Modal */}
      {introTarget && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            {/* Backdrop */}
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={() => setIntroTarget(null)}
            />

            {/* Modal */}
            <div className="inline-block w-full max-w-2xl my-8 overflow-hidden text-left align-middle transition-all transform sm:my-16">
              <IntroductionRequestForm
                sellerId={sellerId}
                buyer={introTarget.buyer}
                onSuccess={handleIntroSent}
                onCancel={() => setIntroTarget(null)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
